import { getNonUmbracoUrl } from "./getNonUmbracoUrl"
import { getUmbracoMediaUrl } from "./getUmbracoMediaUrl"

/**
 * Replaces all <a href="..."> links and <img src="..."> tags in an HTML string.
 * - For links: processes href with getNonUmbracoUrl and getUmbracoMediaUrl
 * - For images: processes src with getUmbracoMediaUrl
 * @param html The HTML string to process
 * @returns The HTML string with updated <a> hrefs and <img> src attributes
 */
export function replaceRichTextLinks(
  html?: string,
  replaceStrong?: "h2" | "h4",
): string {
  if (!html || typeof html !== "string") return ""

  // Process <a> tags
  let processedHtml = html.replace(
    /<a\s+([^>]*href=["'])([^"'>]+)(["'][^>]*)>/gi,
    (match, beforeHref, href, afterHref) => {
      let newHref = getNonUmbracoUrl(href, true)
      newHref = getUmbracoMediaUrl(newHref)
      const GREEN_LINK_COLOR = "#008761" // brandDark
      let attributes = `${beforeHref}${newHref}${afterHref}`

      // Ensure we manipulate (or add) a style attribute for consistent link styling
      const styleAttrRegex = /style=(["'])(.*?)\1/i
      const desiredDeclarations = ["color:#008761", "text-decoration:underline"]

      if (styleAttrRegex.test(attributes)) {
        attributes = attributes.replace(
          styleAttrRegex,
          (styleMatch, quote, content) => {
            // Remove any existing color or text-decoration declarations
            let cleaned = content
              .replace(/color\s*:[^;]+;?/gi, "")
              .replace(/text-decoration\s*:[^;]+;?/gi, "")
              .trim()

              // Collapse multiple spaces and ensure trailing semicolons
              .replace(/\s{2,}/g, " ")

            const finalContent =
              [
                ...desiredDeclarations,
                cleaned, // keep remaining styles (could be empty)
              ]
                .filter(Boolean)
                .join(";")
                .replace(/;+$/g, "") + ";"

            return `style=${quote}${finalContent}${quote}`
          },
        )
      } else {
        // Append new style attribute just before the closing angle bracket (we are still inside replacement before adding '>')
        attributes = `${attributes} style=\"${desiredDeclarations.join(";")};\"`
      }

      // Avoid double application if function is run again: remove duplicate style declarations (idempotency)
      attributes = attributes.replace(
        /(color:#008761;text-decoration:underline;)(?=(?:[^"']|"[^"]*"|'[^']*')*\1)/g,
        "$1",
      )

      return `<a ${attributes}>`
    },
  )

  // Replace line separator characters with spaces
  processedHtml = processedHtml.replace(/\u2028/g, " ")

  // Process <img> tags
  processedHtml = processedHtml.replace(
    /<img\s+([^>]*src=["'])([^"'>]+)(["'][^>]*)>/gi,
    (match, beforeSrc, src, afterSrc) => {
      const newSrc = getUmbracoMediaUrl(src)
      return `<img ${beforeSrc}${newSrc}${afterSrc}>`
    },
  )

  // Remove aria-level attribute from <li> tags
  processedHtml = processedHtml.replace(
    /<li\s+([^>]*)\s*aria-level=["'][^"']*["']\s*([^>]*)>/gi,
    (match, beforeAriaLevel, afterAriaLevel) => {
      const cleanedAttributes = `${beforeAriaLevel} ${afterAriaLevel}`.trim()
      return `<li${cleanedAttributes ? ` ${cleanedAttributes}` : ""}>`
    },
  )

  // Replace <td> with <th> in the first row (header) of tables
  processedHtml = processedHtml.replace(
    /<tbody>\s*<tr>([\s\S]*?)<\/tr>/gi,
    (match, firstRowContent) => {
      // Replace opening <td> tags with <th>
      let updatedContent = firstRowContent.replace(/<td(\s|>)/gi, "<th$1")
      // Replace closing </td> tags with </th>
      updatedContent = updatedContent.replace(/<\/td>/gi, "</th>")
      return `<tbody><tr>${updatedContent}</tr>`
    },
  )

  // Replace <strong> tags with the specified heading tag if replaceStrong is provided
  if (replaceStrong) {
    processedHtml = processedHtml.replace(
      /<strong([^>]*)>(.*?)<\/strong>/gi,
      (match, attributes, content) => {
        return `<${replaceStrong}${attributes}>${content}</${replaceStrong}>`
      },
    )
  }

  return processedHtml
}
